import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface PendingQuizResult {
  hasPendingQuiz: boolean;
  editionId: string | null;
  editionDate: string | null;
  loading: boolean;
}

/**
 * Detects whether the current user has an untaken quiz during Fri–Sun window.
 * Queries quiz_questions for the most recent Friday edition, then checks
 * if the user has answered all questions.
 */
export function usePendingQuiz(): PendingQuizResult {
  const { user } = useAuth();
  const [hasPendingQuiz, setHasPendingQuiz] = useState(false);
  const [editionId, setEditionId] = useState<string | null>(null);
  const [editionDate, setEditionDate] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setHasPendingQuiz(false);
      setEditionId(null);
      setEditionDate(null);
      setLoading(false);
      return;
    }

    const check = async () => {
      try {
        const now = new Date();
        const day = now.getDay(); // 0=Sun, 5=Fri, 6=Sat

        // Only active Fri–Sun
        if (day !== 0 && day !== 5 && day !== 6) {
          setHasPendingQuiz(false);
          setLoading(false);
          return;
        }

        // Find the most recent Friday's edition that has quiz questions
        // Look back up to 7 days to find it
        const lookbackDate = new Date(now);
        lookbackDate.setDate(lookbackDate.getDate() - 7);
        const lookbackStr = lookbackDate.toISOString().split("T")[0];

        // Get recent approved/sent editions
        const { data: recentEditions } = await supabase
          .from("editions")
          .select("id, edition_date, created_at")
          .in("status", ["approved", "sent"])
          .gte("edition_date", lookbackStr)
          .order("created_at", { ascending: false })
          .limit(20);

        if (!recentEditions || recentEditions.length === 0) {
          setHasPendingQuiz(false);
          setLoading(false);
          return;
        }

        // Deduplicate: keep only the latest edition per edition_date
        const seenDates = new Set<string>();
        const editions = recentEditions.filter((e) => {
          if (seenDates.has(e.edition_date)) return false;
          seenDates.add(e.edition_date);
          return true;
        });

        // Find one that's a Friday
        const fridayEdition = editions.find((ed) => {
          const d = new Date(ed.edition_date + "T00:00:00");
          return d.getDay() === 5;
        });

        if (!fridayEdition) {
          setHasPendingQuiz(false);
          setLoading(false);
          return;
        }

        // Check Sunday 11:59 PM cutoff
        const fridayDate = new Date(fridayEdition.edition_date + "T00:00:00");
        const sundayEnd = new Date(fridayDate);
        sundayEnd.setDate(sundayEnd.getDate() + 2);
        sundayEnd.setHours(23, 59, 59, 999);

        if (now > sundayEnd) {
          setHasPendingQuiz(false);
          setLoading(false);
          return;
        }

        // Get quiz questions via RPC (no correct_answer exposed)
        const { data: quizQs } = await supabase
          .rpc("get_quiz_questions", { p_edition_id: fridayEdition.id });

        const totalQuestions = quizQs?.length || 0;
        if (totalQuestions === 0) {
          setHasPendingQuiz(false);
          setLoading(false);
          return;
        }

        // Count user's responses
        const { count: answeredCount } = await supabase
          .from("quiz_responses")
          .select("id", { count: "exact", head: true })
          .eq("user_id", user.id)
          .in("quiz_question_id", quizQs!.map((q: any) => q.id));

        const pending = (answeredCount || 0) < totalQuestions;
        setHasPendingQuiz(pending);
        setEditionId(fridayEdition.id);
        setEditionDate(fridayEdition.edition_date);
      } catch (e) {
        console.error("usePendingQuiz error:", e);
        setHasPendingQuiz(false);
      } finally {
        setLoading(false);
      }
    };

    check();
  }, [user]);

  return { hasPendingQuiz, editionId, editionDate, loading };
}
